import type {
  TelemetryAdapter,
  TelemetryConfig,
  TelemetryUser,
  ErrorContext,
  MessageContext,
} from './types';

let adapters: TelemetryAdapter[] = [];

export function init(config: TelemetryConfig): void {
  adapters = config.adapters;
}

export function identify(user: TelemetryUser): void {
  for (const adapter of adapters) {
    adapter.identify?.(user);
  }
}

export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  for (const adapter of adapters) {
    adapter.trackEvent?.(name, properties);
  }
}

export function captureError(error: Error, context?: ErrorContext): void {
  for (const adapter of adapters) {
    adapter.captureError?.(error, context);
  }
}

export function captureMessage(message: string, context?: MessageContext): void {
  for (const adapter of adapters) {
    adapter.captureMessage?.(message, context);
  }
}

export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level?: 'info' | 'warning' | 'error'
): void {
  for (const adapter of adapters) {
    adapter.addBreadcrumb?.(category, message, data, level);
  }
}

export function captureImageLoadError(
  url: string,
  context?: {
    eventId?: string;
    source?: 'preloader' | 'retryable-src';
    retryCount?: number;
  },
): void {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url;
  }

  let domain: string;
  try {
    domain = new URL(url).hostname;
  } catch {
    domain = 'unknown';
  }

  captureError(new Error(`Image load failed: ${pathname}`), {
    tags: {
      error_type: 'image_load_failure',
      image_source: context?.source ?? 'unknown',
    },
    contexts: {
      image: {
        url,
        event_id: context?.eventId,
        retry_count: context?.retryCount,
      },
    },
    level: 'error',
    fingerprint: ['image-load-failure', domain],
  });
}

export function reset(): void {
  adapters = [];
}
